import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { DriverViewModel } from "@app/shared/models/drivers/DriverViewModel.model";
import { LessorService } from "@app/lessor/shared/lessor.service";
import { DriverPeopleNetSummaryModel } from "@app/shared/models/drivers/DriverPeopleNetSummary.model";

@Component({
  selector: "app-driver-profile",
  templateUrl: "./driver-profile.component.html",
  styleUrls: ["./driver-profile.component.css"],
})
export class DriverProfileComponent implements OnInit, OnChanges {
  @Input() driver: DriverViewModel;
  @Input() isEditMode: boolean;
  @Input() isNewDriver: boolean;
  @Input() states: { abbreviation: string; fullName: string }[];
  @Input() phoneMask: any;
  @Input() dateInputMask: any;
  @Input() peopleNetSummary: DriverPeopleNetSummaryModel | null;

  get isNonAvatarDriver(): boolean {
    return this.driver?.type === "NON-AVATAR DRIVER";
  }

  get isAvatarLocked(): boolean {
    return !this.isEditMode || !this.isNonAvatarDriver;
  }

  @Output() hasChanges = new EventEmitter<boolean>();
  @Output() driverNoUpdated = new EventEmitter<void>();
  @Output() driverTypeChange = new EventEmitter<void>();

  lessorOwners: any[] = [];
  private originalDriver: string;

  constructor(private lessorService: LessorService) {}

  ngOnInit() {
    this.originalDriver = JSON.stringify(this.driver);
    this.loadLessors();
    this.parseSettlementEmails();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["driver"] && !changes["driver"].firstChange) {
      this.originalDriver = JSON.stringify(this.driver);
      this.parseSettlementEmails();
      this.checkForChanges();
    }
  }

  loadLessors() {
    this.lessorService.getActiveLessorList(true).subscribe((response) => {
      this.lessorOwners = response || [];
    });
  }

  onFieldChange() {
    this.checkForChanges();
  }

  onDriverNoUpdate() {
    this.driverNoUpdated.emit();
    this.checkForChanges();
  }

  onDriverTypeChanged() {
    this.driverTypeChange.emit();
    this.checkForChanges();
  }

  private checkForChanges() {
    const currentDriver = JSON.stringify(this.driver);
    const changed = currentDriver !== this.originalDriver;
    this.hasChanges.emit(changed);
  }

  settlementEmails: string[] = [""];

  driverHireDateCounter() {
    if (this.driver?.driverHireDates && Array.isArray(this.driver.driverHireDates)) {
      return new Array(this.driver.driverHireDates.length + 1);
    }
    return new Array(1);
  }

  addDriverHireDate() {
    if (!this.driver.hiredDate) {
      return;
    }
    if (!this.driver.hiredDateTo) {
      this.driver.hiredDateTo = new Date();
    }
    if (!this.driver.driverHireDates || !Array.isArray(this.driver.driverHireDates)) {
      this.driver.driverHireDates = [];
    }
    this.driver.driverHireDates.push({
      hiredDate: this.driver.hiredDate,
      hiredDateTo: this.driver.hiredDateTo,
    });
    this.driver.driverHireDates.sort((a, b) => {
      const aDate = new Date(a.hiredDate).getTime();
      const aDateTo = new Date(a.hiredDateTo).getTime();
      const bDate = new Date(b.hiredDate).getTime();
      const bDateTo = new Date(b.hiredDateTo).getTime();
      return aDate === bDate ? bDateTo - aDateTo : bDate - aDate;
    });
    // Force a new array reference to ensure change detection
    this.driver.driverHireDates = [...this.driver.driverHireDates];
    this.driver.hiredDate = null;
    this.driver.hiredDateTo = null;
    this.onFieldChange();
  }

  removeDriverHireDate(index: number) {
    if (!this.driver.driverHireDates || !Array.isArray(this.driver.driverHireDates)) {
      return;
    }

    const arrayIndex = index - 1;
    if (arrayIndex >= 0 && arrayIndex < this.driver.driverHireDates.length) {
      this.driver.driverHireDates.splice(arrayIndex, 1);
      // Force a new array reference to ensure change detection
      this.driver.driverHireDates = [...this.driver.driverHireDates];
      this.onFieldChange();
    }
  }

  parseSettlementEmails() {
    if (this.driver.settlementEmail) {
      this.settlementEmails = this.driver.settlementEmail
        .split(";")
        .map((email) => email.trim())
        .filter((email) => email.length > 0);
    }
    if (this.settlementEmails.length === 0) {
      this.settlementEmails = [""];
    }
  }

  onSettlementEmailChange() {
    this.driver.settlementEmail = this.settlementEmails
      .filter((email) => email && email.trim().length > 0)
      .join(";");
    this.onFieldChange();
  }

  addSettlementEmail() {
    this.settlementEmails.push("");
  }

  trackByIndex(index: number): number {
    return index;
  }

  removeSettlementEmail(index: number) {
    if (this.settlementEmails.length > 1) {
      this.settlementEmails.splice(index, 1);
      this.onSettlementEmailChange();
    }
  }

  getDateOfLastMovement(): string {
    if (!this.peopleNetSummary?.mostRecentLocation?.dateTime) {
      return "n/a";
    }
    const date = new Date(this.peopleNetSummary.mostRecentLocation.dateTime);
    return date.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  formatDateOnly(dateString: any): string {
    if (!dateString) return "";
    // If it's a string in YYYY-MM-DD format, convert to MM/dd/yyyy
    const str = String(dateString);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split("-");
      return `${month}/${day}/${year}`;
    }
    return str;
  }
}
